import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useAuditCatalogue } from '../../context/AuditCatalogueContext';
import { AuditEntry } from '../../types/audit';
import ActorChip from './ActorChip';
import { formatGst, humanizeEntityType, visibleEntityRoute } from './auditFormat';

interface AuditTableProps {
  rows: AuditEntry[];
  onOpen: (row: AuditEntry) => void;
}

/**
 * Spec 069 AC-B2 — one row per admin action: time (GST), actor, action, entity (linked when the viewer
 * can open it), garage, source. A click anywhere on the row opens the detail drawer; the time cell is a
 * real button so the keyboard path is Tab → Enter/Space.
 */
export default function AuditTable({ rows, onOpen }: AuditTableProps) {
  const { isSuperAdmin, hasScreen } = useAuth();
  const { labelOf } = useAuditCatalogue();

  return (
    <div className="admin-table-wrapper audit-table-wrapper">
      <table className="admin-table audit-table">
        <thead>
          <tr>
            <th scope="col" aria-sort="descending">Time (GST)</th>
            <th scope="col">Admin</th>
            <th scope="col">Action</th>
            <th scope="col">Entity</th>
            <th scope="col">Garage</th>
            <th scope="col">Source</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const route = visibleEntityRoute(row, isSuperAdmin, hasScreen);
            const garageRoute = row.garageId && hasScreen('garages') ? `/garages/${row.garageId}` : null;
            const entityText = row.entityName || (row.entityId ? `…${row.entityId.slice(-8)}` : '—');
            return (
              <tr key={row.id} className="audit-row" onClick={() => onOpen(row)}>
                <td className="audit-cell--time">
                  {/* The row's keyboard handle: Enter / Space open the drawer like a click on the row. */}
                  <button
                    type="button"
                    className="audit-row__open"
                    aria-label={`${row.actionLabel || labelOf(row.action)} on ${entityText}, ${formatGst(row.performedAt)}. Open details`}
                    onClick={(e) => { e.stopPropagation(); onOpen(row); }}
                  >
                    {formatGst(row.performedAt)}
                  </button>
                </td>
                <td><ActorChip role={row.actorRole ?? 'ADMIN'} name={row.actorName} subjectId={row.actorSubjectId} /></td>
                <td>
                  <span className="audit-action-label">{row.actionLabel || labelOf(row.action)}</span>
                  {row.hasChanges && <span className="audit-badge" title="This action recorded field changes">changes</span>}
                </td>
                <td>
                  <span className="audit-entity-type">{humanizeEntityType(row.entityType)}</span>{' '}
                  {route ? (
                    <Link to={route} onClick={(e) => e.stopPropagation()} className="audit-link">{entityText}</Link>
                  ) : (
                    <span>{entityText}</span>
                  )}
                </td>
                <td>
                  {row.garageName || row.garageId ? (
                    garageRoute ? (
                      <Link to={garageRoute} onClick={(e) => e.stopPropagation()} className="audit-link">
                        {row.garageName || `…${row.garageId!.slice(-8)}`}
                      </Link>
                    ) : (
                      <span>{row.garageName || `…${row.garageId!.slice(-8)}`}</span>
                    )
                  ) : '—'}
                </td>
                <td className="audit-cell--muted">{row.sourceService ?? '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
